import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { SubscriptionDiagnosticError } from '../../../domain/diagnostic-errors.entity';
import { InternalMetadataEntity } from '../../../domain/internal-metadata.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import { SubscriptionSelector } from '../../../store';
import { InternalMetadataConfigComponent } from '../../internal-metadata-config/internal-metadata-config.component';

@Component({
	selector: 'app-internal-metadata-popup',
	templateUrl: './internal-metadata-popup.component.html',
	styleUrls: ['./internal-metadata-popup.component.scss'],
	providers: [SubscriptionProvider],
})
export class InternalMetadataPopupComponent<
	T extends { metadata: InternalMetadataEntity }
> extends DcBaseComponent {
	protected readonly DcIcons = DcIcons;

	@ViewChild(InternalMetadataConfigComponent)
	public subscriptionInternalMetadataCmp: InternalMetadataConfigComponent | null =
		null;

	public vo$: Observable<{
		internalMetadata: InternalMetadataEntity;
		error: SubscriptionDiagnosticError | undefined;
		isValid: boolean;
	}>;

	private readonly isValid$ = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly modalService: AppModalService,
		private readonly selector: SubscriptionSelector,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'internal-metadata-popup';

		this.vo$ = combineAll({
			internalMetadata: this.modalData.metadata,
			isValid: this.isValid$,
			error: this.subscriptionProvider.errors$.pipe(
				map((errors) => errors.valueSeq().first())
			),
		});
	}

	public async internalMetadataChanged(): Promise<void> {
		if (!this.subscriptionInternalMetadataCmp) {
			return;
		}

		const initialMetadata = await firstValueFrom(
			this.selector.getInternalMetadata$()
		);

		const currentMetadata =
			await this.subscriptionInternalMetadataCmp.extractFormData();

		const hasChanged = !initialMetadata.isEqualTo(currentMetadata);

		this.isValid$.next(
			hasChanged
				? (await firstValueFrom(this.subscriptionProvider.errors$)).size === 0
				: false
		);
	}

	public async updateAction(): Promise<void> {
		if (!this.subscriptionInternalMetadataCmp) {
			return;
		}

		const metadataConfig =
			await this.subscriptionInternalMetadataCmp.extractFormData();

		this.modalService.closeActiveModal({
			metadataConfig,
		});
	}
}
